import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';

import portfolioService from '../../services/portfolioService';
import exchangeApiService from '../../services/exchangeApiService';
import assetApiService from '../../services/assetApiService';
import symbolService from '../../services/symbolService';
import { useOnboarding } from '../../components/Onboarding/OnboardingProvider';
import { auth } from '../../auth';
import cryptoLogo from '../../assets/logo/logo.png';
import binanceLogo from '../../logos/binance.svg';
import alpacaLogo from '../../assets/logo/alpaca.png';

/* ─── Design tokens — "dark, matte, institutional" (Portfolio handoff v2) ── */
const C = {
  bg: '#0a0c11', card: '#0f1218', input: '#13171f', panel: '#0c0e14',
  bright: '#f2f6ff', primary: '#f4f7fc', body: '#9aa6bd', muted: '#6f7c96',
  muted2: '#7b88a2', faint: '#5e6a82',
  accent: '#4d8fc7', accent2: '#3f6fb0', accentText: '#9cc3e4',
  positive: '#46b88a', negative: '#d97b72', amber: '#d9a441',
  hairline: 'rgba(255,255,255,.06)', binance: '#f0b90b', alpaca: '#fcd535',
};
const MONO = '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, monospace';
const SANS = '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const BTN_GRAD = 'linear-gradient(180deg,#3d5c7b,#33506b)';
// Matte surface — flat, no inset highlight, no glow.
const CARD = { background: C.card, border: `1px solid ${C.hairline}`, borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,.22)' };

const ASSET = {
  BTC: { glyph: '₿', name: 'Bitcoin', color: '#f7931a' },
  ETH: { glyph: 'Ξ', name: 'Ethereum', color: '#a39bff' },
  SOL: { glyph: '◎', name: 'Solana', color: '#14b8a6' },
  BNB: { glyph: 'B', name: 'BNB', color: '#f0b90b' },
  XRP: { glyph: 'X', name: 'XRP', color: '#c9d2e3' },
  ADA: { glyph: 'A', name: 'Cardano', color: '#3468d1' },
  DOGE: { glyph: 'Ð', name: 'Dogecoin', color: '#c2a633' },
  NVDA: { glyph: 'N', name: 'NVIDIA', color: '#9bd64a' },
  AAPL: { glyph: 'A', name: 'Apple', color: '#c9d2e3' },
  TSLA: { glyph: 'T', name: 'Tesla', color: '#e82127' },
};
const assetMeta = (sym) => ASSET[sym] || { glyph: (sym || '?')[0], name: sym, color: C.accent };
const BRAND = {
  Binance: { color: C.binance, tile: 'rgba(240,185,11,.12)', border: 'rgba(240,185,11,.3)' },
  'Binance.US': { color: C.binance, tile: 'rgba(240,185,11,.12)', border: 'rgba(240,185,11,.3)' },
  Alpaca: { color: C.alpaca, tile: 'rgba(252,213,53,.12)', border: 'rgba(252,213,53,.3)' },
};
const brandMeta = (label) => BRAND[label] || { color: C.body, tile: 'rgba(255,255,255,.06)', border: 'rgba(255,255,255,.12)' };
const hexA = (hex, a) => { const n = parseInt(hex.replace('#', ''), 16); return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`; };

/* ─── Formatting ─────────────────────────────────────────────────────────── */
const fmt2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd = (n) => '$' + fmt2(n);
const signedUsd = (n) => (n >= 0 ? '+' : '−') + '$' + fmt2(Math.abs(n));
const signedPct = (n) => (n >= 0 ? '+' : '−') + Math.abs(Number(n) || 0).toFixed(2) + '%';
const shortUsd = (n) => { const a = Math.abs(n || 0), s = n < 0 ? '-' : ''; return a >= 1000 ? `${s}$${(a / 1000).toFixed(a >= 100000 ? 0 : 1)}K` : `${s}$${Math.round(a)}`; };
const priceStr = (p) => '$' + Number(p || 0).toLocaleString('en-US', { maximumFractionDigits: p < 1 ? 6 : 2 });
const qtyStr = (h) => (h.assetClass === 'stock'
  ? `${Number(h.qty).toLocaleString('en-US', { maximumFractionDigits: 2 })} sh`
  : `${Number(h.qty).toLocaleString('en-US', { maximumFractionDigits: 6 })} ${h.symbol}`);

/* ─── Equity chart math ──────────────────────────────────────────────────── */
const N_BY_RANGE = { '1D': 24, '1W': 7, '1M': 30, '1Y': 12 };
const X_LABELS = {
  '1D': ['10:00', '13:00', '16:00'], '1W': ['Mon', 'Wed', 'Fri', 'Sun'],
  '1M': ['4w ago', '3w', '2w', '1w', 'now'], '1Y': ['Q1', 'Q2', 'Q3', 'now'],
};
function buildPath(values) {
  const w = 720, h = 210, pad = 10;
  const vals = values.length >= 2 ? values : [values[0] ?? 0, values[0] ?? 0];
  const min = Math.min(...vals), max = Math.max(...vals), rng = (max - min) || 1, n = vals.length;
  const flat = max === min; // constant portfolio → draw a centered flat line
  const xs = (i) => (i / (n - 1)) * w;
  const ys = (v) => (flat ? h / 2 : h - ((v - min) / rng) * (h - pad * 2) - pad);
  let d = `M${xs(0).toFixed(1)} ${ys(vals[0]).toFixed(1)}`;
  for (let i = 1; i < n; i++) {
    const x0 = xs(i - 1), y0 = ys(vals[i - 1]), x1 = xs(i), y1 = ys(vals[i]), xc = (x0 + x1) / 2;
    d += ` C${xc.toFixed(1)} ${y0.toFixed(1)} ${xc.toFixed(1)} ${y1.toFixed(1)} ${x1.toFixed(1)} ${y1.toFixed(1)}`;
  }
  return { line: d, area: `${d} L${w} ${h} L0 ${h} Z`, lastX: xs(n - 1).toFixed(1), lastY: ys(vals[n - 1]).toFixed(1), firstY: ys(vals[0]).toFixed(1), min, max };
}
function synthValues(seed, n, g, balance) {
  // Smooth line from period start to current value — NO random noise, so an
  // unchanged portfolio renders as a flat line (not a jagged one). When g === 0
  // every point equals `balance`.
  const out = [];
  for (let i = 0; i < n; i++) { const t = i / (n - 1); out.push(100 * (1 + g * t)); }
  const factor = balance / (out[n - 1] || 1);
  return out.map((v) => v * factor);
}

/* ─── Atoms ──────────────────────────────────────────────────────────────── */
const Mono = (props) => <Box component="span" {...props} sx={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums', ...props.sx }} />;
const PulseDot = ({ color = C.positive, size = 5 }) => (
  <Box component="span" sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, animation: 'clp 2.6s ease-in-out infinite', display: 'inline-block' }} />
);
const BinanceMark = ({ s = 22 }) => <Box component="img" src={binanceLogo} alt="Binance" sx={{ width: s, height: s, display: 'block' }} />;
const AlpacaMark = ({ s = 22 }) => <Box component="img" src={alpacaLogo} alt="Alpaca" sx={{ width: s, height: s, display: 'block', borderRadius: '50%' }} />;
// No MEXC brand asset yet — use a themed monogram badge.
// eslint-disable-next-line no-unused-vars -- kept for when MEXC is re-enabled in EXCHANGE_OPTIONS
const MexcMark = ({ s = 22 }) => <Box aria-label="MEXC" sx={{ width: s, height: s, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: '#1972F5', color: '#fff', fontFamily: SANS, fontWeight: 700, fontSize: s * 0.42, lineHeight: 1 }}>M</Box>;
const RefreshIcon = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 4v5h-5" /></svg>);
const EditIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6f7c96" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>);
const LockIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>);

function FilterChip({ active, onClick, dot, children }) {
  return (
    <Box onClick={onClick} sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, px: '12px', py: '5px', borderRadius: '999px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .15s ease',
      color: active ? C.accentText : C.body, background: active ? hexA(C.accent, 0.12) : C.input, border: `1px solid ${active ? hexA(C.accent, 0.38) : 'rgba(255,255,255,.08)'}` }}>
      {dot && <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dot, opacity: active ? 1 : 0.5 }} />}
      {children}
    </Box>
  );
}

const primaryBtnSx = (pad) => ({
  display: 'inline-flex', alignItems: 'center', gap: '9px', fontFamily: SANS, fontWeight: 600, color: '#e7eef6',
  background: BTN_GRAD, border: 'none', borderRadius: '9px', cursor: 'pointer', transition: 'transform .2s ease',
  '&:hover': { transform: 'translateY(-1px)' }, ...pad,
});

/* ─── Component ──────────────────────────────────────────────────────────── */
export default function PortfolioOverview() {
  const routerHistory = useHistory();
  const location = useLocation();
  const { refreshStatus } = useOnboarding();
  const demoRanRef = useRef(false);

  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState(null);
  const [range, setRange] = useState('1M');
  const [series, setSeries] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [editExId, setEditExId] = useState(null);
  // Scale-handling UI state (Portfolio handoff v3): search accounts by name,
  // collapse the account-pill overflow, and collapse the small-balance tail.
  const [exQuery, setExQuery] = useState('');
  const [pillsExpanded, setPillsExpanded] = useState(false);
  const [showDust, setShowDust] = useState(false);
  // Exchange filters drive the WHOLE page. Simulated is ON by default: the demo
  // account the onboarding flow creates IS simulated, so defaulting it off meant
  // a brand-new user landed on a page that hid the only exchange they had. This
  // now matches resetFilters(), which already turned Simulated back on.
  // Any toggle resets scope.
  const [filters, setFilters] = useState({ live: true, sim: true, Binance: true, 'Binance.US': true, Alpaca: true });
  const toggleFilter = (key) => { setFilters((f) => ({ ...f, [key]: !f[key] })); setScope(null); };
  const resetFilters = () => { setFilters({ live: true, sim: true, Binance: true, 'Binance.US': true, Alpaca: true }); setScope(null); setExQuery(''); };
  // Exchange ids deleted client-side — filtered out until the server stops
  // returning them, so a momentarily-stale cached summary can't re-add a row
  // the user just deleted.
  const removedIdsRef = useRef(new Set());

  const loadSummary = useCallback(async (fresh = false) => {
    try {
      const data = await portfolioService.summary({ fresh });
      const removed = removedIdsRef.current;
      const incoming = data.exchanges || [];
      const present = new Set(incoming.map((e) => e.id));
      for (const id of [...removed]) if (!present.has(id)) removed.delete(id); // server caught up
      const exchanges = incoming.filter((e) => !removed.has(e.id));
      // If we filtered a just-deleted row out of a stale response, recompute the
      // aggregate so the header total stays consistent until the server catches up.
      let aggregate = data.aggregate;
      if (exchanges.length !== incoming.length) {
        aggregate = {
          ...data.aggregate,
          balance: exchanges.reduce((s, e) => s + (e.balance || 0), 0),
          change24Usd: exchanges.reduce((s, e) => s + (e.change24Usd || 0), 0),
          exchangeCount: exchanges.length,
        };
      }
      const next = { ...data, exchanges, aggregate };
      setModel(next);
      setError(null);
      // Returned so callers that need the freshly-priced holdings immediately
      // (the demo flow locks them) don't have to wait for a state round-trip.
      return next;
    } catch (err) {
      console.error('[portfolio] summary failed', err);
      setError('Could not load your portfolio. Check your connection and try again.');
      return null;
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // One-click demo exchange: create a simulated account, seed a starter balance
  // so the portfolio isn't empty, then drop into the add-assets/lock drawer.
  const createDemoExchange = useCallback(async () => {
    try {
      const userId = parseInt(localStorage.getItem('userId'), 10);
      const email = localStorage.getItem('email');
      const created = await exchangeApiService.create({
        name: 'Demo Account', exchange: 'binance.com', simulated: true, user_id: userId, email,
      });
      const exId = created?.id ?? created?.exchange?.id;
      if (exId != null) {
        const seed = [
          { symbol: 'USDT', amount: 10000 },
          { symbol: 'BTC', amount: 0.1 },
          { symbol: 'ETH', amount: 1.5 },
        ];
        for (const s of seed) {
          try { await assetApiService.create({ exchange_id: exId, symbol: s.symbol, amount: s.amount }); } catch (_) {}
        }
      }
      setLoading(true);
      const summary = await loadSummary(true);
      if (exId != null) { setScope(exId); setEditExId(exId); }

      // Lock the seeded holdings straight away. Locking captures the current
      // prices as the cost-basis baseline, which is what makes the simulated
      // account's P&L meaningful — leaving it to the user meant a fresh demo
      // sat in the "NEEDS LOCK" state showing no P&L. Mirrors lockHoldings().
      let locked = false;
      const createdEx = (summary?.exchanges || []).find((e) => e.id === exId);
      const rows = createdEx?.holdings || [];
      if (rows.length) {
        const per = {};
        let total = 0;
        // Every holding must be priced or the baseline would be wrong; if the
        // price feed hasn't caught up we skip and let the user lock manually.
        const priced = rows.every((h) => (Number(h.price) || 0) > 0);
        if (priced) {
          for (const h of rows) {
            per[h.symbol] = { amount: h.qty, price: Number(h.price), value: h.value };
            total += h.value;
          }
          const ts = Date.now();
          try {
            await exchangeApiService.lock(exId, {
              user_id: userId, assets_locked: true, lock_ts: ts,
              lock_snapshot: { ts, total, per },
            });
            locked = true;
            await loadSummary(true); // reflect assetsLocked in the UI
          } catch (lockErr) {
            console.error('[portfolio] demo auto-lock failed', lockErr);
          }
        }
      }

      // Refresh AFTER the lock attempt, and bypass the summary cache, so the
      // checklist ticks both "Create an exchange" and "Add & lock assets" in
      // one go rather than leaving the second unticked until a later reload.
      try { refreshStatus && refreshStatus({ fresh: true }); } catch (_) {}

      toast.success(locked
        ? 'Demo exchange created with a sample balance. Holdings locked — P&L is now tracking.'
        : 'Demo exchange created with a sample balance. Lock your holdings to start tracking P&L.');
      return true;
    } catch (err) {
      console.error('[portfolio] createDemoExchange failed', err);
      toast.error('Could not create the demo exchange. Please try again.');
      return false;
    }
  }, [loadSummary, refreshStatus]);

  // Welcome-modal / checklist deep-link: ?onboard=demo runs the demo flow once.
  //
  // `next=quickbot` means we were sent here mid-Quick-Bot because the user had
  // no exchange. Once one exists, hand the journey back instead of leaving them
  // on this page to find Quick Bot again. Entry points that pass no `next`
  // (WelcomeModal, GettingStarted) keep landing here as before, and a failed
  // creation stays put so we don't bounce back to a Quick Bot with no exchange.
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('onboard') === 'demo' && !demoRanRef.current) {
      demoRanRef.current = true;
      const next = params.get('next');
      // clear the param immediately so a reload can't re-run the demo flow
      routerHistory.replace('/profile/portfolio');
      createDemoExchange().then((ok) => {
        if (ok && next === 'quickbot') routerHistory.replace('/profile/traderform?quickbot=1');
      });
    }
  }, [location.search, createDemoExchange, routerHistory]);

  useEffect(() => {
    let active = true;
    portfolioService.history(range, scope)
      .then((res) => { if (active) setSeries(Array.isArray(res?.series) ? res.series : []); })
      .catch(() => { if (active) setSeries([]); });
    return () => { active = false; };
  }, [range, scope]);

  const view = useMemo(() => {
    if (!model) return null;
    const all = model.exchanges || [];
    // Apply Type + Exchange filters — this set drives the entire page.
    const typeSel = (e) => (e.simulated ? filters.sim : filters.live);
    const brandSel = (label) => filters[label] !== false; // unknown brands stay visible
    const q = (exQuery || '').trim().toLowerCase();
    const nameSel = (e) => !q || (e.name || '').toLowerCase().includes(q);
    const exchanges = all.filter((e) => typeSel(e) && brandSel(e.label) && nameSel(e));
    // If the scoped exchange got filtered out, fall back to All.
    const effScope = (scope != null && exchanges.some((e) => e.id === scope)) ? scope : null;
    const inScope = effScope == null ? exchanges : exchanges.filter((e) => e.id === effScope);
    const agg = (key) => inScope.reduce((s, e) => s + (e[key] || 0), 0);
    const balance = agg('balance');
    const change24Usd = agg('change24Usd');
    const change24Pct = (balance - change24Usd) !== 0 ? (change24Usd / (balance - change24Usd)) * 100 : 0;
    const pnlParts = inScope.filter((e) => e.pnlUsd != null);
    const pnlUsd = pnlParts.length ? pnlParts.reduce((s, e) => s + e.pnlUsd, 0) : null;
    const pnlBase = pnlParts.reduce((s, e) => s + (e.balance - e.pnlUsd), 0);
    const pnlPct = pnlUsd != null && pnlBase > 0 ? (pnlUsd / pnlBase) * 100 : null;
    // Aggregate the same asset held on multiple exchanges into one row, keeping
    // the per-exchange breakdown (qty/value) in `sources` so the UI can show
    // where it's held. Within a single-exchange scope each asset appears once.
    const holdingMap = new Map();
    for (const e of inScope) {
      for (const h of (e.holdings || [])) {
        let agg = holdingMap.get(h.symbol);
        if (!agg) {
          agg = { symbol: h.symbol, assetClass: h.assetClass, price: h.price, change24Pct: h.change24Pct, qty: 0, value: 0, sources: [] };
          holdingMap.set(h.symbol, agg);
        }
        agg.qty += Number(h.qty) || 0;
        agg.value += Number(h.value) || 0;
        if ((!agg.price || agg.price <= 0) && h.price > 0) agg.price = h.price;
        agg.sources.push({ exName: e.name, qty: Number(h.qty) || 0, value: Number(h.value) || 0 });
      }
    }
    const holdings = [...holdingMap.values()]
      // Hide dust / zero-value assets (e.g. unpriced fiat, sub-cent balances) —
      // anything that would render as $0.00 is noise in the overview.
      .filter((h) => h.value >= 0.005)
      .map((h) => ({
        ...h,
        price: h.qty > 0 ? h.value / h.qty : h.price, // blended unit price (same asset → same price)
        sources: h.sources.sort((a, b) => b.value - a.value),
      }));
    holdings.sort((a, b) => b.value - a.value);
    const scopeTotal = holdings.reduce((s, h) => s + h.value, 0) || 1;
    // Collapse the long tail of tiny holdings into an expandable "small balances"
    // group. The cutoff is RELATIVE (< DUST_PCT % of the in-scope total) so it
    // adapts to portfolio size — a $200 coin is noise in a $5M book but a real
    // position in a $2k one, where a fixed dollar threshold would wrongly hide it.
    // Never collapse everything: if that would leave no main rows (e.g. many equal
    // small holdings), keep them all visible.
    const DUST_PCT = 1;
    let mainHoldings = holdings.filter((h) => (h.value / scopeTotal) * 100 >= DUST_PCT);
    let dustHoldings = holdings.filter((h) => (h.value / scopeTotal) * 100 < DUST_PCT);
    if (mainHoldings.length === 0) { mainHoldings = holdings; dustHoldings = []; }
    const dustTotal = dustHoldings.reduce((s, h) => s + h.value, 0);
    const cryptoCount = holdings.filter((h) => h.assetClass === 'crypto').length;
    const stockCount = holdings.filter((h) => h.assetClass === 'stock').length;
    const scopeName = effScope == null ? 'All exchanges' : (exchanges.find((e) => e.id === effScope)?.name || 'Exchange');
    return { exchanges, totalExchanges: all.length, scope: effScope, inScope, balance, change24Usd, change24Pct, pnlUsd, pnlPct, holdings, mainHoldings, dustHoldings, dustTotal, scopeTotal, cryptoCount, stockCount, scopeName, pos: change24Usd >= 0 };
  }, [model, scope, filters, exQuery]);

  const chart = useMemo(() => {
    if (!view) return null;
    const n = N_BY_RANGE[range];
    let values, periodPct;
    if (series && series.length >= 2) {
      values = series.map((p) => p.v);
      periodPct = values[0] ? ((values[values.length - 1] - values[0]) / values[0]) * 100 : 0;
    } else {
      const seed = view.scope == null ? 3.1 : ((view.scope % 9) + 1) * 2.3;
      const baseG = (view.pnlPct != null ? view.pnlPct : view.change24Pct) / 100;
      const g = { '1D': view.change24Pct / 100, '1W': baseG * 0.2, '1M': baseG * 0.46, '1Y': baseG }[range] || baseG;
      values = synthValues(seed, n, g, view.balance || 1);
      periodPct = g * 100;
    }
    const p = buildPath(values);
    const up = periodPct >= 0;
    return { ...p, periodPct, up, color: up ? C.accent : C.negative, yHi: shortUsd(p.max), yMid: shortUsd((p.max + p.min) / 2), yLo: shortUsd(p.min), xLabels: X_LABELS[range] };
  }, [view, series, range]);

  const alloc = useMemo(() => {
    if (!view) return null;
    // Cap the donut + legend at the top 5 assets and roll the rest into a single
    // "Others (N)" slice, so a portfolio with dozens of assets stays readable.
    const OTHERS = '#3a475e';
    const top = view.holdings.slice(0, 5);
    const rest = view.holdings.slice(5);
    const restVal = rest.reduce((s, h) => s + h.value, 0);
    const segsSrc = top.map((h) => ({ color: assetMeta(h.symbol).color, label: h.symbol, value: h.value }));
    if (restVal > 0) segsSrc.push({ color: OTHERS, label: `Others (${rest.length})`, value: restVal });
    let acc = 0;
    const segs = segsSrc.map((s) => { const a = acc; acc += (s.value / view.scopeTotal) * 100; return `${s.color} ${a.toFixed(1)}% ${Math.min(acc, 100).toFixed(1)}%`; });
    const pctStr = (v) => { const q = (v / view.scopeTotal) * 100; return q >= 1 ? `${q.toFixed(q >= 10 ? 0 : 1)}%` : '<1%'; };
    return {
      gradient: segsSrc.length ? `conic-gradient(${segs.join(', ')})` : `conic-gradient(${C.hairline} 0% 100%)`,
      legend: segsSrc.map((s) => ({ symbol: s.label, color: s.color, pct: pctStr(s.value) })),
      center: shortUsd(view.balance),
    };
  }, [view]);

  const logout = () => { try { auth.logout(); } catch (_) {} routerHistory.push('/sign-in'); };
  const refresh = async () => { setLoading(true); await loadSummary(true); };

  if (loading && !model) return <Shell><CenterNote text="Loading your portfolio…" /></Shell>;
  if (error && !model) return <Shell nav={<Nav onLogout={logout} go={routerHistory.push} />}><CenterNote text={error} action={{ label: 'Retry', onClick: refresh }} /></Shell>;

  const exchanges = view.exchanges;
  // "Empty" means the user truly has no exchanges — NOT that filters hid them all
  // (that case keeps the filter bar visible with a "no match / reset" state).
  const isEmpty = view.totalExchanges === 0;
  const pc = view.pos ? C.positive : C.negative;
  const editEx = editExId != null ? exchanges.find((e) => e.id === editExId) : null;
  const scopedEx = view.scope != null ? exchanges.find((e) => e.id === view.scope) : null;
  const scopedSim = !!(scopedEx && scopedEx.simulated);
  const scopedEmpty = !!(scopedEx && (scopedEx.holdings?.length || 0) === 0);
  const noMatch = view.totalExchanges > 0 && exchanges.length === 0;
  // Scope-pill overflow: show the first PILL_MAX accounts + a "+N more" toggle,
  // but always keep the currently-scoped account visible even if it's past the cap.
  const PILL_MAX = 6;
  const cappedPills = pillsExpanded ? exchanges : exchanges.slice(0, PILL_MAX);
  const shownPills = (view.scope != null && !cappedPills.some((e) => e.id === view.scope))
    ? [...cappedPills, exchanges.find((e) => e.id === view.scope)].filter(Boolean)
    : cappedPills;
  const hiddenPillCount = exchanges.length - Math.min(PILL_MAX, exchanges.length);
  // Holdings table shows the main rows; the small-balance tail is revealed on toggle.
  const holdingRows = showDust ? view.holdings : view.mainHoldings;

  return (
    <Shell nav={<Nav onLogout={logout} go={routerHistory.push} />}>
      <ToastContainer position="bottom-right" theme="dark" autoClose={3500} />
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: '20px', md: '40px' }, pt: '36px', pb: '72px' }}>
        {isEmpty ? (
          <EmptyState onConnect={() => setConnectOpen(true)} onDemo={createDemoExchange} />
        ) : (
          <>
            {/* HEADER */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: '28px', flexWrap: 'wrap', gap: '18px' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '12px' }}>
                  <Mono sx={{ fontSize: '12px', letterSpacing: '.2em', color: C.accent }}>PORTFOLIO OVERVIEW</Mono>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <PulseDot /><Mono sx={{ fontSize: '10px', letterSpacing: '.12em', color: C.muted }}>LIVE</Mono>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <Mono sx={{ fontSize: '40px', fontWeight: 600, letterSpacing: '-.02em', color: C.bright }}>{usd(view.balance)}</Mono>
                  <Mono sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '14px', fontWeight: 600, color: pc, bgcolor: hexA(pc, 0.07), border: `1px solid ${hexA(pc, 0.18)}`, px: '11px', py: '5px', borderRadius: '8px' }}>
                    {view.pos ? '▲' : '▼'} {signedPct(view.change24Pct)} today
                  </Mono>
                </Box>
                <Typography sx={{ fontSize: '14px', color: C.muted, mt: '8px' }}>
                  {view.scope == null
                    ? `Aggregated across ${exchanges.length} exchange${exchanges.length === 1 ? '' : 's'} · updated just now`
                    : `${view.scopeName} · ${view.inScope[0]?.label} · ${view.inScope[0]?.simulated ? 'Simulated' : 'Live'} · updated just now`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Box component="button" onClick={refresh} sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: SANS, fontSize: '14px', fontWeight: 600, color: '#cdd6e8', bgcolor: 'transparent', px: '18px', py: '12px', border: '1px solid rgba(255,255,255,.14)', borderRadius: '9px', cursor: 'pointer', transition: 'border-color .2s, color .2s', '&:hover': { borderColor: hexA(C.accent, 0.5), color: '#fff' } }}>
                  <RefreshIcon /> Refresh
                </Box>
                <Box component="button" onClick={() => setConnectOpen(true)} data-tour="connect-exchange" sx={primaryBtnSx({ px: '24px', py: '13px', fontSize: '14.5px' })}>
                  <Box component="span" sx={{ fontSize: '18px', lineHeight: 1, mt: '-1px' }}>+</Box> Connect exchange
                </Box>
              </Box>
            </Box>

            {/* SCOPE SELECTOR */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '22px', flexWrap: 'wrap' }}>
              <Mono sx={{ fontSize: '11px', letterSpacing: '.14em', color: C.muted, mr: '6px' }}>VIEWING</Mono>
              <ScopePill active={view.scope == null} onClick={() => setScope(null)}>All exchanges</ScopePill>
              {shownPills.map((e) => (
                <ScopePill key={e.id} active={view.scope === e.id} onClick={() => setScope(view.scope === e.id ? null : e.id)}>{e.name}</ScopePill>
              ))}
              {(hiddenPillCount > 0 || pillsExpanded) && (
                <Box onClick={() => setPillsExpanded((v) => !v)} sx={{ display: 'inline-flex', alignItems: 'center', fontSize: '12.5px', fontWeight: 600, px: '13px', py: '6px', borderRadius: '999px', cursor: 'pointer', whiteSpace: 'nowrap', color: '#8593ad', background: 'transparent', border: '1px dashed rgba(255,255,255,.16)', transition: 'color .15s ease', '&:hover': { color: '#cdd6e8' } }}>
                  {pillsExpanded ? 'Show less' : `+${hiddenPillCount} more`}
                </Box>
              )}
            </Box>

            {/* KPI ROW */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: '16px', mb: '16px' }}>
              <Kpi title="Total balance" value={usd(view.balance)}
                sub={view.scope == null ? `Across ${exchanges.length} exchange${exchanges.length === 1 ? '' : 's'}` : `${view.inScope[0]?.label} · ${view.inScope[0]?.simulated ? 'Simulated' : 'Live'}`} />
              <Kpi title="24h change" value={signedUsd(view.change24Usd)} valueColor={pc} sub={signedPct(view.change24Pct)} subColor={pc} subMono />
              <Kpi title="Total P&L"
                value={view.pnlUsd != null ? signedUsd(view.pnlUsd) : '—'}
                valueColor={view.pnlUsd == null ? C.bright : (view.pnlUsd >= 0 ? C.positive : C.negative)}
                sub={view.pnlUsd != null ? `${signedPct(view.pnlPct)} all-time` : 'No cost basis yet'}
                subColor={view.pnlUsd == null ? C.muted : (view.pnlUsd >= 0 ? C.positive : C.negative)} subMono={view.pnlUsd != null} />
              <Kpi title="Open positions" value={String(view.holdings.length)} sub={`${view.stockCount} stocks · ${view.cryptoCount} crypto`} />
            </Box>

            {/* EQUITY + ALLOCATION */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.62fr 1fr' }, gap: '16px', mb: '16px' }}>
              <Box sx={{ ...CARD, p: '22px 24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '6px', flexWrap: 'wrap', gap: '10px' }}>
                  <Box>
                    <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#e8edf7' }}>Portfolio value</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mt: '7px' }}>
                      <Mono sx={{ fontSize: '13.5px', fontWeight: 600, color: chart.up ? C.positive : C.negative }}>{signedPct(chart.periodPct)}</Mono>
                      <Typography sx={{ fontSize: '12.5px', color: C.muted }}>this period</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: '4px', background: C.input, border: '1px solid rgba(255,255,255,.07)', borderRadius: '9px', p: '4px' }}>
                    {['1D', '1W', '1M', '1Y'].map((r) => (
                      <Box key={r} component="button" onClick={() => setRange(r)} sx={{ fontFamily: MONO, fontSize: '12px', fontWeight: 600, px: '12px', py: '6px', borderRadius: '6px', border: 'none', cursor: 'pointer', transition: 'all .15s ease', color: range === r ? '#bcd6ea' : '#8593ad', background: range === r ? 'rgba(255,255,255,.06)' : 'transparent' }}>{r}</Box>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '12px', mt: '14px' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 210, py: '4px', minWidth: 42, textAlign: 'right', fontFamily: MONO, fontVariantNumeric: 'tabular-nums', fontSize: '10.5px', color: C.faint }}>
                    <span>{chart.yHi}</span><span>{chart.yMid}</span><span>{chart.yLo}</span>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ color: chart.color }}>
                      <svg viewBox="0 0 720 210" preserveAspectRatio="none" style={{ width: '100%', height: 210, display: 'block' }}>
                        <defs><linearGradient id="pf-area" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="currentColor" stopOpacity="0.22" /><stop offset="1" stopColor="currentColor" stopOpacity="0" /></linearGradient></defs>
                        <g stroke="rgba(255,255,255,.05)" strokeWidth="1"><line x1="0" y1="10" x2="720" y2="10" /><line x1="0" y1="105" x2="720" y2="105" /><line x1="0" y1="200" x2="720" y2="200" /></g>
                        <line x1="0" y1={chart.firstY} x2="720" y2={chart.firstY} stroke="rgba(255,255,255,.18)" strokeWidth="1" strokeDasharray="4 5" />
                        <path d={chart.area} fill="url(#pf-area)" />
                        <path d={chart.line} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx={chart.lastX} cy={chart.lastY} r="3.5" fill="currentColor" />
                      </svg>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '9px', fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.04em', color: C.faint }}>
                      {chart.xLabels.map((x, i) => <span key={i}>{x}</span>)}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ ...CARD, p: '22px 24px' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#e8edf7', mb: '20px' }}>Allocation</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                  <Box sx={{ position: 'relative', width: 130, height: 130, flex: 'none' }}>
                    <Box sx={{ width: 130, height: 130, borderRadius: '50%', background: alloc.gradient }} />
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 88, height: 88, borderRadius: '50%', background: C.card, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Mono sx={{ fontSize: '18px', fontWeight: 600, color: C.bright }}>{alloc.center}</Mono>
                      <Typography sx={{ fontSize: '10.5px', color: C.muted, mt: '2px' }}>{view.holdings.length} assets</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 120, display: 'flex', flexDirection: 'column', gap: '11px' }}>
                    {alloc.legend.length === 0 && <Typography sx={{ fontSize: '13px', color: C.muted }}>No holdings in view.</Typography>}
                    {alloc.legend.map((l) => (
                      <Box key={l.symbol} sx={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '13px' }}>
                        <Box component="span" sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: l.color }} />
                        <Box component="span" sx={{ color: '#c9d2e3', flex: 1 }}>{l.symbol}</Box>
                        <Mono sx={{ color: '#8593ad' }}>{l.pct}</Mono>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* YOUR EXCHANGES — filter bar + table */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '14px', gap: '12px', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                <Typography sx={{ fontSize: '19px', fontWeight: 700, color: C.bright }}>Your exchanges</Typography>
                <Mono sx={{ fontSize: '12.5px', color: C.muted }}>{exchanges.length} of {view.totalExchanges} shown</Mono>
              </Box>
              <Box component="button" onClick={() => setConnectOpen(true)} sx={primaryBtnSx({ px: '18px', py: '10px', fontSize: '13.5px', gap: '8px' })}>
                <Box component="span" sx={{ fontSize: '16px', lineHeight: 1, mt: '-1px' }}>+</Box> Connect
              </Box>
            </Box>

            {/* Filter bar — Type + Exchange (drive the whole page) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', mb: '14px', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Mono sx={{ fontSize: '10.5px', letterSpacing: '.14em', color: C.muted, mr: '2px' }}>TYPE</Mono>
                <FilterChip active={filters.live} onClick={() => toggleFilter('live')} dot={C.positive}>Live</FilterChip>
                <FilterChip active={filters.sim} onClick={() => toggleFilter('sim')} dot={C.accent}>Simulated</FilterChip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Mono sx={{ fontSize: '10.5px', letterSpacing: '.14em', color: C.muted, mr: '2px' }}>EXCHANGE</Mono>
                <FilterChip active={filters.Binance} onClick={() => toggleFilter('Binance')}>Binance</FilterChip>
                <FilterChip active={filters['Binance.US']} onClick={() => toggleFilter('Binance.US')}>Binance.US</FilterChip>
                <FilterChip active={filters.Alpaca} onClick={() => toggleFilter('Alpaca')}>Alpaca</FilterChip>
              </Box>
              <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', gap: '8px', background: C.input, border: '1px solid rgba(255,255,255,.08)', borderRadius: '8px', px: '12px', py: '7px' }}>
                <Box component="svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6f7c96" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></Box>
                <Box component="input" value={exQuery} onChange={(e) => setExQuery(e.target.value)} placeholder="Search accounts…" aria-label="Search accounts" sx={{ background: 'transparent', border: 'none', outline: 'none', color: '#e8edf7', fontSize: '13px', width: 150, fontFamily: SANS, '&::placeholder': { color: C.muted } }} />
              </Box>
            </Box>

            <Box sx={{ ...CARD, overflow: 'hidden', mb: '16px' }}>
              {noMatch ? (
                <Box sx={{ p: '40px 24px', textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '14px', color: C.body, mb: '14px' }}>No exchanges match your filters.</Typography>
                  <Box component="button" onClick={resetFilters} sx={{ fontFamily: SANS, fontSize: '13px', fontWeight: 600, color: '#cdd6e8', bgcolor: 'transparent', px: '16px', py: '9px', border: '1px solid rgba(255,255,255,.16)', borderRadius: '9px', cursor: 'pointer', '&:hover': { borderColor: hexA(C.accent, 0.5), color: '#fff' } }}>Reset filters</Box>
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: 760 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.7fr 1.2fr 1fr 1.1fr 0.85fr 40px', gap: '14px', alignItems: 'center', p: '14px 24px', borderBottom: `1px solid ${C.hairline}`, fontFamily: MONO, fontSize: '11px', letterSpacing: '.1em', color: C.muted }}>
                      <span>ACCOUNT</span><span>EXCHANGE</span><span>TYPE</span><span style={{ textAlign: 'right' }}>BALANCE</span><span style={{ textAlign: 'right' }}>24H</span><span />
                    </Box>
                    {exchanges.map((e) => (
                      <ExchangeRow key={e.id} ex={e} selected={view.scope === e.id}
                        onClick={() => setScope(view.scope === e.id ? null : e.id)}
                        onEdit={() => setEditExId(e.id)} />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* HOLDINGS — table */}
            <Box sx={{ ...CARD, p: '22px 24px', mb: '30px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', mb: '18px', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                  <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#e8edf7' }}>Holdings</Typography>
                  <Typography sx={{ fontSize: '12.5px', color: C.muted }}>{view.scopeName}</Typography>
                </Box>
                {scopedSim && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {!scopedEx.assetsLocked && (
                      <Box component="button" onClick={() => setEditExId(view.scope)} sx={primaryBtnSx({ px: '16px', py: '9px', fontSize: '13px', gap: '7px' })}>
                        <Box component="span" sx={{ fontSize: '16px', lineHeight: 1, mt: '-1px' }}>+</Box> Add holding
                      </Box>
                    )}
                    {!scopedEx.assetsLocked && (
                      <Box component="button" onClick={() => setEditExId(view.scope)} sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: SANS, fontSize: '13px', fontWeight: 600, color: C.amber, background: hexA(C.amber, 0.1), border: `1px solid ${hexA(C.amber, 0.4)}`, px: '14px', py: '9px', borderRadius: '9px', cursor: 'pointer', transition: 'all .15s ease', '&:hover': { background: hexA(C.amber, 0.18) } }}>
                        <LockIcon /> Lock assets
                      </Box>
                    )}
                    <Box component="button" onClick={refresh} sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: SANS, fontSize: '13px', fontWeight: 600, color: '#cdd6e8', background: 'transparent', border: '1px solid rgba(255,255,255,.14)', px: '14px', py: '9px', borderRadius: '9px', cursor: 'pointer', transition: 'border-color .2s, color .2s', '&:hover': { borderColor: hexA(C.accent, 0.5), color: '#fff' } }}>
                      <RefreshIcon /> Refresh
                    </Box>
                  </Box>
                )}
              </Box>
              {scopedEx && scopedEmpty ? (
                scopedSim ? (
                  <Box sx={{ border: `1.5px dashed ${hexA(C.accent, 0.4)}`, background: hexA(C.accent, 0.04), borderRadius: '12px', p: { xs: '36px 20px', md: '52px 24px' }, textAlign: 'center' }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '50%', mx: 'auto', mb: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', color: C.accent, background: hexA(C.accent, 0.1), border: `1px solid ${hexA(C.accent, 0.3)}`, lineHeight: 1 }}>+</Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: C.bright, mb: '8px' }}>Add assets to get started</Typography>
                    <Typography sx={{ fontSize: '13.5px', color: C.body, maxWidth: 430, mx: 'auto', mb: '22px', lineHeight: 1.6 }}>
                      “{scopedEx.name}” is a simulated exchange with no holdings yet. Add at least one asset to start tracking its value, allocation, and P&L.
                    </Typography>
                    <Box component="button" onClick={() => setEditExId(view.scope)} sx={primaryBtnSx({ px: '24px', py: '13px', fontSize: '14px', gap: '8px' })}>
                      <Box component="span" sx={{ fontSize: '17px', lineHeight: 1, mt: '-1px' }}>+</Box> Add your first asset
                    </Box>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '13.5px', color: C.muted, py: '28px', textAlign: 'center' }}>No holdings in this account yet.</Typography>
                )
              ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: 720 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1.1fr 1.2fr 1.2fr 1fr 1.4fr', gap: '14px', p: '0 4px 12px', borderBottom: `1px solid ${C.hairline}`, fontFamily: MONO, fontSize: '11px', letterSpacing: '.1em', color: C.muted }}>
                    <span>ASSET</span><span style={{ textAlign: 'right' }}>PRICE</span><span style={{ textAlign: 'right' }}>HOLDINGS</span><span style={{ textAlign: 'right' }}>VALUE</span><span style={{ textAlign: 'right' }}>24H</span><span>ALLOCATION</span>
                  </Box>
                  {view.holdings.length === 0 && (
                    <Typography sx={{ fontSize: '13.5px', color: C.muted, py: '22px', textAlign: 'center' }}>No holdings in this view yet.</Typography>
                  )}
                  {holdingRows.map((h, i) => {
                    const m = assetMeta(h.symbol);
                    const allocRaw = (h.value / view.scopeTotal) * 100;
                    const allocLabel = allocRaw >= 1 ? `${Math.round(allocRaw)}%` : '<1%';
                    const allocW = Math.max(allocRaw, 0.6);
                    const up = (h.change24Pct || 0) >= 0;
                    return (
                      <Box key={`${h.symbol}-${h.exName}-${i}`} sx={{ display: 'grid', gridTemplateColumns: '2fr 1.1fr 1.2fr 1.2fr 1fr 1.4fr', gap: '14px', alignItems: 'center', p: '14px 4px', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Box component="span" sx={{ width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '14px', flex: 'none', background: hexA(m.color, 0.12), color: m.color, border: `1px solid ${hexA(m.color, 0.35)}` }}>{m.glyph}</Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#e8edf7' }}>{h.symbol}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
                              <Typography sx={{ fontSize: '12px', color: C.muted }}>{m.name}</Typography>
                              {(() => {
                                const unit = h.assetClass === 'stock' ? 'sh' : h.symbol;
                                const chip = { fontSize: '10.5px', color: '#8593ad', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', px: '7px', py: '1px', borderRadius: '5px', whiteSpace: 'nowrap' };
                                const shown = h.sources.slice(0, 3);
                                const rest = h.sources.slice(3);
                                return (
                                  <>
                                    {shown.map((s, si) => (
                                      <Box key={si} component="span" title={`${Number(s.qty).toLocaleString('en-US', { maximumFractionDigits: 6 })} ${unit} on ${s.exName}`} sx={chip}>{s.exName}</Box>
                                    ))}
                                    {rest.length > 0 && (
                                      <Box component="span" title={rest.map((s) => `${Number(s.qty).toLocaleString('en-US', { maximumFractionDigits: 6 })} ${unit} on ${s.exName}`).join(' · ')} sx={chip}>+{rest.length}</Box>
                                    )}
                                  </>
                                );
                              })()}
                            </Box>
                          </Box>
                        </Box>
                        <Mono sx={{ fontSize: '13.5px', color: '#c9d2e3', textAlign: 'right' }}>{priceStr(h.price)}</Mono>
                        <Mono sx={{ fontSize: '13.5px', color: C.body, textAlign: 'right' }}>{qtyStr(h)}</Mono>
                        <Mono sx={{ fontSize: '13.5px', color: C.bright, fontWeight: 600, textAlign: 'right' }}>{usd(h.value)}</Mono>
                        <Mono sx={{ fontSize: '13px', fontWeight: 600, color: up ? C.positive : C.negative, textAlign: 'right' }}>{signedPct(h.change24Pct)}</Mono>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Box sx={{ flex: 1, height: 6, borderRadius: '3px', background: 'rgba(255,255,255,.06)', overflow: 'hidden' }}>
                            <Box sx={{ width: `${allocW}%`, height: '100%', borderRadius: '3px', background: m.color }} />
                          </Box>
                          <Mono sx={{ fontSize: '12px', color: '#8593ad', width: 34, textAlign: 'right' }}>{allocLabel}</Mono>
                        </Box>
                      </Box>
                    );
                  })}
                  {view.dustHoldings.length > 0 && (
                    <Box onClick={() => setShowDust((v) => !v)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', p: '14px 4px 4px', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600, color: '#8593ad', transition: 'color .15s ease', '&:hover': { color: '#cdd6e8' } }}>
                      {showDust ? 'Hide small balances' : `Show ${view.dustHoldings.length} small balance${view.dustHoldings.length === 1 ? '' : 's'} · ${usd(view.dustTotal)} total`}
                      <Box component="span" sx={{ display: 'flex', transform: showDust ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>
                        <Box component="svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      <ConnectDrawer open={connectOpen} onClose={() => setConnectOpen(false)}
        onConnected={(created) => {
          setConnectOpen(false);
          (async () => {
            setLoading(true); await loadSummary(true);
            // Drop straight into the new simulated exchange so the user can add assets.
            if (created?.simulated && created?.id != null) { setScope(created.id); setEditExId(created.id); }
          })();
        }} />
      <EditExchangeDrawer ex={editEx} onClose={() => setEditExId(null)}
        onRefresh={() => loadSummary(true)}
        onChanged={() => { setEditExId(null); refresh(); }}
        onRemoved={(id) => { removedIdsRef.current.add(id); setEditExId(null); if (scope === id) setScope(null); refresh(); }} />
    </Shell>
  );
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */
function Shell({ nav, children }) {
  return (
    <Box sx={{ minHeight: '100vh', background: C.bg, color: C.primary, fontFamily: SANS, fontSize: '14px', lineHeight: 1.45, WebkitFontSmoothing: 'antialiased', '& *': { boxSizing: 'border-box' }, '@keyframes clp': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 1 } } }}>
      {nav}
      {children}
    </Box>
  );
}

function Nav({ onLogout, go }) {
  const link = { color: C.body, cursor: 'pointer', fontSize: '14.5px', fontWeight: 500, transition: 'color .2s', '&:hover': { color: '#e8edf7' } };
  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: '20px', md: '40px' }, py: '15px', background: 'rgba(11,14,21,.82)', backdropFilter: 'blur(14px)', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '20px', md: '46px' } }}>
        <Box component="img" src={cryptoLogo} alt="Cryptologics-AI" sx={{ height: 34, width: 'auto', display: 'block', cursor: 'pointer' }} onClick={() => go('/profile/portfolio')} />
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '30px' }}>
          <Box component="span" sx={{ color: '#fff', position: 'relative', pb: '4px', borderBottom: `2px solid ${C.accent}`, fontSize: '14.5px', fontWeight: 500, cursor: 'pointer' }}>Portfolio</Box>
          <Box component="span" sx={link} onClick={() => go('/profile/traderform')}>AutoTrader</Box>
          <Box component="span" sx={link} onClick={() => go('/profile/scanners')}>Scanners</Box>
          <Box component="span" sx={link} onClick={() => go('/profile/about')}>About</Box>
        </Box>
      </Box>
      <Box component="button" onClick={onLogout} sx={{ fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, color: '#cdd6e8', bgcolor: 'transparent', px: '20px', py: '9px', border: '1px solid rgba(255,255,255,.16)', borderRadius: '8px', cursor: 'pointer', transition: 'border-color .2s, color .2s', '&:hover': { borderColor: hexA(C.negative, 0.5), color: '#fff' } }}>Logout</Box>
    </Box>
  );
}

function ScopePill({ active, onClick, children }) {
  return (
    <Box onClick={onClick} sx={{ fontSize: '12.5px', fontWeight: 600, px: '13px', py: '6px', borderRadius: '999px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .15s ease',
      color: active ? C.accentText : C.body, background: active ? hexA(C.accent, 0.12) : C.input, border: `1px solid ${active ? hexA(C.accent, 0.38) : 'rgba(255,255,255,.08)'}` }}>{children}</Box>
  );
}

function Kpi({ title, value, valueColor = C.bright, sub, subColor = C.muted, subMono }) {
  return (
    <Box sx={{ ...CARD, p: '20px 22px' }}>
      <Mono sx={{ fontSize: '11px', letterSpacing: '.12em', textTransform: 'uppercase', color: C.muted2, mb: '13px', display: 'block' }}>{title}</Mono>
      <Mono sx={{ fontSize: '24px', fontWeight: 600, letterSpacing: '-.01em', color: valueColor, display: 'block' }}>{value}</Mono>
      <Box sx={{ mt: '7px', fontSize: '12.5px', color: subColor, ...(subMono ? { fontFamily: MONO, fontVariantNumeric: 'tabular-nums' } : {}) }}>{sub}</Box>
    </Box>
  );
}

function ExchangeRow({ ex, selected, onClick, onEdit }) {
  const up = (ex.change24Pct || 0) >= 0;
  const bm = brandMeta(ex.label);
  const sim = ex.simulated;
  const typeColor = sim ? C.accent : C.positive;
  return (
    <Box onClick={onClick} sx={{ display: 'grid', gridTemplateColumns: '1.7fr 1.2fr 1fr 1.1fr 0.85fr 40px', gap: '14px', alignItems: 'center', p: '16px 24px', borderBottom: '1px solid rgba(255,255,255,.04)',
      borderLeft: `3px solid ${selected ? C.accent : 'transparent'}`, background: selected ? hexA(C.accent, 0.06) : 'transparent', cursor: 'pointer', transition: 'background .15s ease', '&:hover': { background: selected ? hexA(C.accent, 0.06) : 'rgba(255,255,255,.03)' } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Typography sx={{ fontSize: '14.5px', fontWeight: 600, color: selected ? '#fff' : C.bright, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.name}</Typography>
          {ex.simulated && (ex.holdings?.length || 0) === 0 && (
            <Mono title="This simulated exchange has no holdings yet" sx={{ flex: 'none', fontSize: '9.5px', letterSpacing: '.06em', fontWeight: 600, color: C.amber, background: hexA(C.amber, 0.12), border: `1px solid ${hexA(C.amber, 0.35)}`, px: '6px', py: '2px', borderRadius: '5px', whiteSpace: 'nowrap' }}>NEEDS ASSETS</Mono>
          )}
          {ex.simulated && (ex.holdings?.length || 0) > 0 && !ex.assetsLocked && (
            <Mono title="Lock holdings to set the cost-basis baseline and track P&L" sx={{ flex: 'none', fontSize: '9.5px', letterSpacing: '.06em', fontWeight: 600, color: C.amber, background: hexA(C.amber, 0.12), border: `1px solid ${hexA(C.amber, 0.35)}`, px: '6px', py: '2px', borderRadius: '5px', whiteSpace: 'nowrap' }}>NEEDS LOCK</Mono>
          )}
        </Box>
        <Typography sx={{ fontSize: '12px', color: C.muted }}>{ex.accountType === 'paper' ? 'Test Account' : 'Account'} · #{ex.id}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        <Box component="span" sx={{ width: 28, height: 28, borderRadius: '7px', flex: 'none', background: bm.tile, border: `1px solid ${bm.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontWeight: 700, fontSize: '13px', color: bm.color }}>{(ex.label || '?')[0]}</Box>
        <Typography sx={{ fontSize: '13.5px', color: '#c9d2e3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.label}</Typography>
      </Box>
      <Box>
        <Mono sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', letterSpacing: '.06em', fontWeight: 600, color: typeColor, background: hexA(typeColor, 0.12), border: `1px solid ${hexA(typeColor, 0.3)}`, px: '10px', py: '4px', borderRadius: '6px' }}>
          <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: typeColor }} />{sim ? 'SIMULATED' : 'LIVE'}
        </Mono>
      </Box>
      <Mono sx={{ fontSize: '14.5px', fontWeight: 600, color: C.bright, textAlign: 'right' }}>{usd(ex.balance)}</Mono>
      <Mono sx={{ fontSize: '13px', fontWeight: 600, color: up ? C.positive : C.negative, textAlign: 'right' }}>{signedPct(ex.change24Pct)}</Mono>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }} onClick={(e) => { e.stopPropagation(); onEdit(); }}>
        <Box sx={{ display: 'flex', cursor: 'pointer', opacity: 0.8, '&:hover': { opacity: 1 } }}><EditIcon /></Box>
      </Box>
    </Box>
  );
}

function CenterNote({ text, action }) {
  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', px: '40px', py: '120px', textAlign: 'center' }}>
      <Typography sx={{ color: C.body, fontSize: '15px', mb: action ? '18px' : 0 }}>{text}</Typography>
      {action && <Box component="button" onClick={action.onClick} sx={primaryBtnSx({ px: '22px', py: '11px', fontSize: '14px' })}>{action.label}</Box>}
    </Box>
  );
}

function EmptyState({ onConnect, onDemo }) {
  return (
    <Box sx={{ ...CARD, p: { xs: '48px 24px', md: '72px 56px' }, textAlign: 'center', mt: '24px' }}>
      <Mono sx={{ fontSize: '12px', letterSpacing: '.2em', color: C.accent, display: 'block', mb: '16px' }}>PORTFOLIO OVERVIEW</Mono>
      <Typography sx={{ fontSize: '26px', fontWeight: 700, color: C.bright, mb: '12px' }}>Connect an exchange to begin</Typography>
      <Typography sx={{ fontSize: '15px', color: C.body, maxWidth: 460, mx: 'auto', mb: '28px', lineHeight: 1.6 }}>
        New here? Spin up a risk-free demo account in one click — or link Binance, Binance.US, or Alpaca — to see your balance, allocation, and live holdings here.
      </Typography>
      <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <Box component="button" onClick={onDemo} data-tour="connect-exchange" sx={primaryBtnSx({ px: '28px', py: '14px', fontSize: '15px' })}>
          <Box component="span" sx={{ fontSize: '16px', lineHeight: 1 }}>⚡</Box> Create demo exchange
        </Box>
        <Box component="button" onClick={onConnect} sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: SANS, fontSize: '15px', fontWeight: 600, color: '#cdd6e8', bgcolor: 'transparent', px: '24px', py: '14px', border: '1px solid rgba(255,255,255,.14)', borderRadius: '10px', cursor: 'pointer', '&:hover': { borderColor: hexA(C.accent, 0.5), color: '#fff' } }}>
          Connect a real exchange
        </Box>
      </Box>
    </Box>
  );
}

/* ─── Connect slide-over (wired to real exchangeApiService) ──────────────── */
const EXCHANGE_OPTIONS = [
  { key: 'binance.com', label: 'Binance', mark: <BinanceMark s={22} /> },
  { key: 'binance.us', label: 'Binance.US', mark: <BinanceMark s={22} />, suffix: '.US' },
  // MEXC hidden from the selector until live verification is complete. Backend
  // support is in place — re-enable by uncommenting (mark: <MexcMark s={22} />).
  // { key: 'mexc', label: 'MEXC', mark: <MexcMark s={22} /> },
  { key: 'alpaca', label: 'Alpaca', mark: <AlpacaMark s={22} /> },
];

function ConnectDrawer({ open, onClose, onConnected }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('simulated');
  const [exchangeKey, setExchangeKey] = useState('binance.com');
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => { setName(''); setType('simulated'); setExchangeKey('binance.com'); setApiKey(''); setSecret(''); setBusy(false); };
  const close = () => { if (!busy) { reset(); onClose(); } };

  const submit = async () => {
    if (!name.trim()) { toast.error('Give this connection a name.'); return; }
    setBusy(true);
    try {
      let created = null;
      if (type === 'simulated') {
        created = await exchangeApiService.create({ name: name.trim(), exchange: exchangeKey, simulated: true, user_id: parseInt(localStorage.getItem('userId'), 10), email: localStorage.getItem('email') });
        toast.success('Simulated exchange created');
      } else {
        if (!apiKey.trim() || !secret.trim()) { toast.error('API key and secret are required for a live connection.'); setBusy(false); return; }
        await exchangeApiService.addExchange({ email: localStorage.getItem('email'), exchange: exchangeKey, apiKey: apiKey.trim(), secretKey: secret.trim(), name: name.trim(), userId: parseInt(localStorage.getItem('userId'), 10) });
        toast.success('Live exchange connected');
      }
      reset();
      onConnected(type === 'simulated' ? { id: created?.id ?? created?.exchange?.id, simulated: true } : { simulated: false });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not connect exchange. Check your details and try again.');
      setBusy(false);
    }
  };

  const fieldSx = { fontSize: '14.5px', color: '#e8edf7', background: C.input, border: '1px solid rgba(255,255,255,.1)', borderRadius: '9px', px: '15px', py: '13px', width: '100%', outline: 'none', fontFamily: SANS, '&::placeholder': { color: C.faint }, '&:focus': { borderColor: hexA(C.accent, 0.5) } };
  const label = { display: 'block', fontSize: '12.5px', color: '#8593ad', mb: '9px' };

  return (
    <>
      <Box onClick={close} sx={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(5,8,16,.6)', backdropFilter: 'blur(2px)', transition: 'opacity .25s ease', opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none' }} />
      <Box sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 70, width: 440, maxWidth: '92vw', background: C.panel, borderLeft: '1px solid rgba(255,255,255,.09)', boxShadow: '-30px 0 80px rgba(0,0,0,.5)', transition: 'transform .3s cubic-bezier(.4,0,.2,1)', display: 'flex', flexDirection: 'column', transform: open ? 'translateX(0)' : 'translateX(110%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '22px 26px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
          <Box>
            <Mono sx={{ fontSize: '11px', letterSpacing: '.18em', color: C.accent, mb: '6px', display: 'block' }}>NEW CONNECTION</Mono>
            <Typography sx={{ fontSize: '19px', fontWeight: 700, color: C.bright }}>Connect exchange</Typography>
          </Box>
          <Box onClick={close} sx={{ width: 32, height: 32, borderRadius: '8px', border: '1px solid rgba(255,255,255,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.body, cursor: 'pointer', fontSize: '18px', transition: 'background .2s, color .2s', '&:hover': { background: 'rgba(255,255,255,.06)', color: '#fff' } }}>✕</Box>
        </Box>

        <Box sx={{ p: '26px', display: 'flex', flexDirection: 'column', gap: '24px', overflowY: 'auto' }}>
          <Box>
            <Box component="label" sx={label}>Exchange name</Box>
            <Box component="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My Binance account" sx={fieldSx} />
          </Box>
          <Box>
            <Box component="label" sx={label}>Connection type</Box>
            <Box component="select" value={type} onChange={(e) => setType(e.target.value)} sx={{ ...fieldSx, appearance: 'none', cursor: 'pointer' }}>
              <option value="simulated">Simulated Exchange</option>
              <option value="real">Live Exchange</option>
            </Box>
            <Typography sx={{ fontSize: '12px', color: C.muted, mt: '8px' }}>
              {type === 'simulated' ? 'Paper-trade with virtual funds. Switch to Live to use real API keys.' : 'Trades with real funds using your exchange API keys.'}
            </Typography>
          </Box>
          {type === 'real' && (
            <>
              <Box>
                <Box component="label" sx={label}>API key</Box>
                <Box component="input" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Paste your API key" sx={fieldSx} />
              </Box>
              <Box>
                <Box component="label" sx={label}>API secret</Box>
                <Box component="input" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Paste your API secret" sx={fieldSx} />
              </Box>
            </>
          )}
          <Box>
            <Box component="label" sx={label}>Exchange</Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {EXCHANGE_OPTIONS.map((opt) => {
                const sel = exchangeKey === opt.key;
                return (
                  <Box key={opt.key} onClick={() => setExchangeKey(opt.key)} sx={{ display: 'flex', alignItems: 'center', gap: '12px', px: '15px', py: '13px', borderRadius: '10px', cursor: 'pointer', transition: 'border-color .2s',
                    background: sel ? hexA(C.accent, 0.07) : C.input, border: `1px solid ${sel ? hexA(C.accent, 0.45) : 'rgba(255,255,255,.1)'}`, '&:hover': { borderColor: sel ? hexA(C.accent, 0.45) : 'rgba(255,255,255,.22)' } }}>
                    {opt.mark}
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 600, color: sel ? C.bright : '#e8edf7', flex: 1 }}>
                      {opt.label.replace('.US', '')}{opt.suffix && <Box component="span" sx={{ color: C.body }}>{opt.suffix}</Box>}
                    </Typography>
                    {sel && <Box component="span" sx={{ width: 18, height: 18, borderRadius: '50%', background: BTN_GRAD, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e7eef6', fontSize: '11px', fontWeight: 800 }}>✓</Box>}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 'auto', p: '20px 26px', borderTop: '1px solid rgba(255,255,255,.07)' }}>
          <Box component="button" onClick={submit} disabled={busy} sx={{ ...primaryBtnSx({ p: '14px' }), width: '100%', justifyContent: 'center', fontSize: '15px', opacity: busy ? 0.7 : 1, cursor: busy ? 'default' : 'pointer', '&:hover': busy ? {} : { transform: 'translateY(-1px)' } }}>
            {busy ? 'Connecting…' : 'Connect'}
          </Box>
        </Box>
      </Box>
    </>
  );
}

/* ─── Edit / manage an existing exchange (rename · rotate keys · remove) ──── */
const ASSET_SUGGESTIONS = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA', 'DOGE', 'USDT', 'USDC'];

function EditExchangeDrawer({ ex, onClose, onChanged, onRemoved, onRefresh }) {
  // Locking here is what completes the "Add & lock assets" onboarding step, so
  // this drawer has to tell the checklist about it — onRefresh only reloads the
  // portfolio model, not the onboarding status.
  const { refreshStatus } = useOnboarding();
  const open = !!ex;
  const [name, setName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  // Local holdings list for simulated exchanges (add/remove assets).
  const [assets, setAssets] = useState([]);
  const [newSym, setNewSym] = useState('');
  const [newAmt, setNewAmt] = useState('');
  const [addingAsset, setAddingAsset] = useState(false);
  const [availSymbols, setAvailSymbols] = useState([]); // assets available on this exchange (from Redis-backed symbol list)
  const [symOpen, setSymOpen] = useState(false); // themed asset-symbol dropdown
  // Themed asset-symbol suggestions (replaces the unstyleable native <datalist>).
  const symSource = availSymbols.length ? availSymbols : ASSET_SUGGESTIONS;
  const symQuery = newSym.trim().toUpperCase();
  const filteredSyms = (symQuery
    ? [...symSource.filter((s) => s.startsWith(symQuery)), ...symSource.filter((s) => !s.startsWith(symQuery) && s.includes(symQuery))]
    : symSource).slice(0, 60);

  useEffect(() => {
    if (ex) {
      setName(ex.name || ''); setApiKey(''); setSecret(''); setConfirmRemove(false); setBusy(false);
      setNewSym(''); setNewAmt(''); setAddingAsset(false);
      setAssets((ex.holdings || []).map((h) => ({ id: h.id, symbol: h.symbol, qty: h.qty, assetClass: h.assetClass })));
    }
  }, [ex]);

  // Load the assets available on this exchange (Redis-backed symbol catalog) so
  // the add form offers the real, full list rather than a hand-picked few.
  useEffect(() => {
    let active = true;
    if (ex && ex.simulated && ex.exchange) {
      symbolService.get(ex.exchange).then((rows) => {
        if (!active) return;
        const bases = [...new Set((rows || [])
          .map((r) => String(r.base_asset ?? r.baseAsset ?? '').toUpperCase().replace(/[^A-Z0-9]/g, ''))
          .filter(Boolean))].sort();
        setAvailSymbols(bases);
      }).catch(() => { if (active) setAvailSymbols([]); });
    } else {
      setAvailSymbols([]);
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ex?.id, ex?.simulated, ex?.exchange]);

  const addAsset = async () => {
    const symbol = newSym.trim().toUpperCase();
    const amount = parseFloat(String(newAmt).replace(',', '.').trim());
    if (!symbol) { toast.error('Enter an asset symbol.'); return; }
    if (!Number.isFinite(amount) || amount <= 0) { toast.error('Amount must be a positive number.'); return; }
    if (assets.some((a) => String(a.symbol).toUpperCase() === symbol)) { toast.error(`${symbol} is already in this exchange.`); return; }
    // Note: availSymbols (from the symbol service) is advisory only — used for
    // suggestions, not to block adds, since that catalog can be partial/offline.
    setAddingAsset(true);
    try {
      const created = await assetApiService.create({ exchange_id: ex.id, symbol, amount });
      setAssets((prev) => [...prev, { id: created?.id, symbol, qty: amount, assetClass: ex.assetClass }]);
      setNewSym(''); setNewAmt('');
      toast.success(`${symbol} added`);
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Failed to add asset.');
    } finally { setAddingAsset(false); }
  };

  const removeAsset = async (a) => {
    if (a.id == null) { toast.error('Reload to manage this holding.'); return; }
    try {
      await assetApiService.delete(a.id);
      setAssets((prev) => prev.filter((x) => x.id !== a.id));
      toast.success(`${a.symbol} removed`);
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove asset.');
    }
  };

  const locked = !!ex?.assetsLocked;

  // Lock captures the current holdings (priced from the summary) as the cost
  // basis baseline — this is what makes the simulated exchange's all-time P&L
  // meaningful and freezes the holdings.
  const lockHoldings = async () => {
    const userId = parseInt(localStorage.getItem('userId'), 10);
    if (!Number.isFinite(userId)) { toast.error('Not signed in.'); return; }
    const rows = ex.holdings || [];
    if (!rows.length) { toast.error('Add at least one asset before locking.'); return; }
    const per = {}; let total = 0;
    for (const h of rows) {
      const price = Number(h.price) || 0;
      if (price <= 0) { toast.error(`No price available for ${h.symbol} — can’t lock yet.`); return; }
      per[h.symbol] = { amount: h.qty, price, value: h.value };
      total += h.value;
    }
    const ts = Date.now();
    setBusy(true);
    try {
      await exchangeApiService.lock(ex.id, { user_id: userId, assets_locked: true, lock_ts: ts, lock_snapshot: { ts, total, per } });
      toast.success('Holdings locked');
      onRefresh && onRefresh();
      try { refreshStatus && refreshStatus({ fresh: true }); } catch (_) {}
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to lock holdings.');
    } finally { setBusy(false); }
  };

  const unlockHoldings = async () => {
    setBusy(true);
    try {
      await exchangeApiService.update(ex.id, { name: ex.name, exchange: ex.exchange, assets_locked: false });
      toast.success('Holdings unlocked');
      try { refreshStatus && refreshStatus({ fresh: true }); } catch (_) {}
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to unlock holdings.');
    } finally { setBusy(false); }
  };

  const close = () => { if (!busy) onClose(); };

  const save = async () => {
    if (!ex) return;
    if (!name.trim()) { toast.error('Name can’t be empty.'); return; }
    const wantKeyUpdate = !ex.simulated && (apiKey.trim() || secret.trim());
    if (wantKeyUpdate && (!apiKey.trim() || !secret.trim())) { toast.error('Enter both API key and secret to update keys.'); return; }
    setBusy(true);
    try {
      if (name.trim() !== ex.name) {
        await exchangeApiService.update(ex.id, { name: name.trim(), exchange: ex.exchange });
      }
      if (wantKeyUpdate) {
        await exchangeApiService.updateKeys(ex.id, { apiKey: apiKey.trim(), secretKey: secret.trim() });
      }
      toast.success('Exchange updated');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not update exchange.');
      setBusy(false);
    }
  };

  // Actually performs the delete (called by the explicit "Delete" button).
  const remove = async () => {
    if (!ex) return;
    setBusy(true);
    try {
      await exchangeApiService.delete(ex.id);
      toast.success('Exchange removed');
      onRemoved(ex.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not remove exchange.');
      setBusy(false);
    }
  };

  const fieldSx = { fontSize: '14.5px', color: '#e8edf7', background: C.input, border: '1px solid rgba(255,255,255,.1)', borderRadius: '9px', px: '15px', py: '13px', width: '100%', outline: 'none', fontFamily: SANS, '&::placeholder': { color: C.faint }, '&:focus': { borderColor: hexA(C.accent, 0.5) } };
  const label = { display: 'block', fontSize: '12.5px', color: '#8593ad', mb: '9px' };

  return (
    <>
      <Box onClick={close} sx={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(5,8,16,.6)', backdropFilter: 'blur(2px)', transition: 'opacity .25s ease', opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none' }} />
      <Box sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 70, width: 440, maxWidth: '92vw', background: C.panel, borderLeft: '1px solid rgba(255,255,255,.09)', boxShadow: '-30px 0 80px rgba(0,0,0,.5)', transition: 'transform .3s cubic-bezier(.4,0,.2,1)', display: 'flex', flexDirection: 'column', transform: open ? 'translateX(0)' : 'translateX(110%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '22px 26px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
          <Box>
            <Mono sx={{ fontSize: '11px', letterSpacing: '.18em', color: C.accent, mb: '6px', display: 'block' }}>MANAGE</Mono>
            <Typography sx={{ fontSize: '19px', fontWeight: 700, color: C.bright }}>Edit exchange</Typography>
          </Box>
          <Box onClick={close} sx={{ width: 32, height: 32, borderRadius: '8px', border: '1px solid rgba(255,255,255,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.body, cursor: 'pointer', fontSize: '18px', transition: 'background .2s, color .2s', '&:hover': { background: 'rgba(255,255,255,.06)', color: '#fff' } }}>✕</Box>
        </Box>

        <Box sx={{ p: '26px', display: 'flex', flexDirection: 'column', gap: '24px', overflowY: 'auto' }}>
          {ex && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Box component="span" sx={{ width: 28, height: 28, borderRadius: '7px', flex: 'none', background: brandMeta(ex.label).tile, border: `1px solid ${brandMeta(ex.label).border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontWeight: 700, fontSize: '13px', color: brandMeta(ex.label).color }}>{(ex.label || '?')[0]}</Box>
              <Typography sx={{ fontSize: '13.5px', color: '#c9d2e3' }}>{ex.label} · {ex.simulated ? 'Simulated' : 'Live'}</Typography>
            </Box>
          )}
          <Box>
            <Box component="label" sx={label}>Exchange name</Box>
            <Box component="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Account name" sx={fieldSx} />
          </Box>
          {ex && !ex.simulated && (
            <>
              <Box>
                <Box component="label" sx={label}>API key <Box component="span" sx={{ color: C.faint }}>(leave blank to keep current)</Box></Box>
                <Box component="input" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="New API key" sx={fieldSx} />
              </Box>
              <Box>
                <Box component="label" sx={label}>API secret</Box>
                <Box component="input" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="New API secret" sx={fieldSx} />
              </Box>
            </>
          )}

          {/* Holdings management — simulated exchanges only */}
          {ex && ex.simulated && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '9px' }}>
                <Box component="label" sx={{ ...label, mb: 0 }}>Holdings</Box>
                {locked && (
                  <Box component="button" onClick={unlockHoldings} disabled={busy} sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: MONO, fontSize: '10.5px', fontWeight: 600, letterSpacing: '.06em', color: C.accent, background: hexA(C.accent, 0.12), border: `1px solid ${hexA(C.accent, 0.3)}`, px: '10px', py: '4px', borderRadius: '6px', cursor: busy ? 'default' : 'pointer' }}>🔒 LOCKED · UNLOCK</Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: locked ? 0 : '12px' }}>
                {assets.length === 0 && <Typography sx={{ fontSize: '12.5px', color: C.muted }}>No assets yet — add one below.</Typography>}
                {assets.map((a) => {
                  const m = assetMeta(a.symbol);
                  return (
                    <Box key={a.id ?? a.symbol} sx={{ display: 'flex', alignItems: 'center', gap: '10px', background: C.input, border: '1px solid rgba(255,255,255,.08)', borderRadius: '9px', px: '12px', py: '9px' }}>
                      <Box component="span" sx={{ width: 26, height: 26, borderRadius: '50%', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '12px', background: hexA(m.color, 0.12), color: m.color, border: `1px solid ${hexA(m.color, 0.35)}` }}>{m.glyph}</Box>
                      <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#e8edf7', flex: 'none' }}>{a.symbol}</Typography>
                      <Mono sx={{ fontSize: '13px', color: C.body, flex: 1, textAlign: 'right' }}>{Number(a.qty).toLocaleString('en-US', { maximumFractionDigits: 6 })}</Mono>
                      {!locked && (
                        <Box component="button" onClick={() => removeAsset(a)} aria-label={`Remove ${a.symbol}`} sx={{ flex: 'none', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid rgba(255,255,255,.1)', borderRadius: '7px', color: C.muted, cursor: 'pointer', fontSize: '15px', lineHeight: 1, transition: 'all .15s ease', '&:hover': { color: '#fff', borderColor: hexA(C.negative, 0.5) } }}>✕</Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
              {locked ? (
                <Typography sx={{ fontSize: '12px', color: C.muted, mt: '10px' }}>Holdings are locked as the P&L baseline. Unlock to add or remove assets.</Typography>
              ) : (
                <>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                  <Box sx={{ position: 'relative', flex: '0 0 42%' }}>
                    <Box component="input" value={newSym}
                      onChange={(e) => { setNewSym(e.target.value); setSymOpen(true); }}
                      onFocus={() => setSymOpen(true)}
                      onBlur={() => setSymOpen(false)}
                      placeholder="Search asset" autoComplete="off"
                      sx={{ ...fieldSx, textTransform: 'uppercase' }} />
                    {symOpen && filteredSyms.length > 0 && (
                      <Box sx={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 5, maxHeight: 200, overflowY: 'auto', background: C.card, border: `1px solid ${C.hairline}`, borderRadius: '9px', boxShadow: '0 16px 36px -10px rgba(0,0,0,.7)' }}>
                        {filteredSyms.map((s) => {
                          const m = assetMeta(s);
                          return (
                            <Box key={s} onMouseDown={(e) => { e.preventDefault(); setNewSym(s); setSymOpen(false); }}
                              sx={{ display: 'flex', alignItems: 'center', gap: '9px', px: '12px', py: '8px', cursor: 'pointer', transition: 'background .12s ease', '&:hover': { background: hexA(C.accent, 0.1) } }}>
                              <Box component="span" sx={{ width: 20, height: 20, borderRadius: '50%', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, background: hexA(m.color, 0.12), color: m.color, border: `1px solid ${hexA(m.color, 0.35)}` }}>{m.glyph}</Box>
                              <Mono sx={{ fontSize: '13px', color: '#e8edf7' }}>{s}</Mono>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                  <Box component="input" type="number" min="0" step="any" value={newAmt} onChange={(e) => setNewAmt(e.target.value)} placeholder="Amount" sx={{ ...fieldSx, flex: 1 }} />
                  <Box component="button" onClick={addAsset} disabled={addingAsset} sx={{ ...primaryBtnSx({ px: '16px' }), flex: 'none', justifyContent: 'center', fontSize: '13.5px', opacity: addingAsset ? 0.7 : 1, cursor: addingAsset ? 'default' : 'pointer' }}>{addingAsset ? '…' : 'Add'}</Box>
                </Box>
                <Typography sx={{ fontSize: '11.5px', color: C.muted, mt: '8px' }}>
                  {availSymbols.length ? `${availSymbols.length} assets available on ${ex.label} — start typing to search.` : 'Type an asset symbol (e.g. BTC) and an amount.'}
                </Typography>

                {/* Prominent nudge: lock holdings to set the P&L baseline */}
                {assets.length > 0 && (
                  <Box sx={{ mt: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', p: '14px 16px', borderRadius: '10px', background: hexA(C.amber, 0.1), border: `1px solid ${hexA(C.amber, 0.4)}` }}>
                    <Box sx={{ flex: 1, minWidth: 180 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '3px' }}>
                        <Box sx={{ color: C.amber, display: 'flex' }}><LockIcon /></Box>
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#f0dcb4' }}>Lock holdings to track P&L</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: C.muted, lineHeight: 1.5 }}>
                        Locking sets the cost-basis baseline so this account starts reporting all-time P&L. You can unlock later to edit.
                      </Typography>
                    </Box>
                    <Box component="button" onClick={lockHoldings} disabled={busy} sx={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: SANS, fontSize: '13.5px', fontWeight: 700, color: '#1a1206', background: C.amber, border: 'none', borderRadius: '9px', px: '18px', py: '11px', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1, boxShadow: `0 6px 16px ${hexA(C.amber, 0.22)}`, transition: 'transform .15s ease', '&:hover': busy ? {} : { transform: 'translateY(-1px)' } }}>
                      <LockIcon /> {busy ? 'Locking…' : 'Lock holdings'}
                    </Box>
                  </Box>
                )}
                </>
              )}
            </Box>
          )}

          <Box sx={{ pt: '4px' }}>
            {!confirmRemove ? (
              <Box component="button" onClick={() => setConfirmRemove(true)} disabled={busy} sx={{ width: '100%', textAlign: 'center', fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, color: C.negative, background: 'transparent', border: `1px solid ${hexA(C.negative, 0.35)}`, borderRadius: '9px', p: '12px', cursor: 'pointer', transition: 'all .15s ease', '&:hover': { background: hexA(C.negative, 0.08), borderColor: hexA(C.negative, 0.6) } }}>
                Remove exchange
              </Box>
            ) : (
              <Box sx={{ border: `1px solid ${hexA(C.negative, 0.4)}`, background: hexA(C.negative, 0.08), borderRadius: '10px', p: '14px' }}>
                <Typography sx={{ fontSize: '13px', color: '#f4d9d4', mb: '12px', lineHeight: 1.5 }}>
                  Delete “{ex?.name}”? This permanently removes the exchange and its holdings.
                </Typography>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <Box component="button" onClick={() => setConfirmRemove(false)} disabled={busy} sx={{ flex: 1, textAlign: 'center', fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, color: '#cdd6e8', background: 'transparent', border: '1px solid rgba(255,255,255,.16)', borderRadius: '9px', p: '11px', cursor: busy ? 'default' : 'pointer' }}>
                    Cancel
                  </Box>
                  <Box component="button" onClick={remove} disabled={busy} sx={{ flex: 1, textAlign: 'center', fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, color: '#fff', background: hexA(C.negative, busy ? 0.6 : 0.9), border: `1px solid ${hexA(C.negative, 0.9)}`, borderRadius: '9px', p: '11px', cursor: busy ? 'default' : 'pointer' }}>
                    {busy ? 'Deleting…' : 'Delete'}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 'auto', p: '20px 26px', borderTop: '1px solid rgba(255,255,255,.07)' }}>
          <Box component="button" onClick={save} disabled={busy} sx={{ ...primaryBtnSx({ p: '14px' }), width: '100%', justifyContent: 'center', fontSize: '15px', opacity: busy ? 0.7 : 1, cursor: busy ? 'default' : 'pointer', '&:hover': busy ? {} : { transform: 'translateY(-1px)' } }}>
            {busy ? 'Saving…' : 'Save changes'}
          </Box>
        </Box>
      </Box>
    </>
  );
}
